// NorthArrow.tsx — 3D north-direction indicator for ground plane.
// Renders a flat arrow on Y=0 pointing along -Z (North).
// Positioned one major grid unit back-left of datum (negative X and Z).

import { useMemo } from 'react';
import * as THREE from 'three';

const ARROW_COLOR = new THREE.Color(0xcc2222);
const LABEL_COLOR = new THREE.Color(0xffffff);

/** Flat arrow shape — tip at y=0, tail at y=length.
 *  Drawn reversed so the rendered arrow points toward the N label
 *  after the parent group's Y-rotation. */
function makeArrowShape(length: number, headW: number, shaftW: number, headL: number): THREE.Shape {
  const hl = headL;        // head length (head extends from tip at y=0 up to y=hl)
  const sw = shaftW / 2;
  const hw = headW / 2;

  const s = new THREE.Shape();
  // shaft (rectangle from y=hl to y=length)
  s.moveTo(-sw, hl);
  s.lineTo(-sw, length);
  s.lineTo(sw, length);
  s.lineTo(sw, hl);
  // head (triangle from y=hl down to tip at y=0)
  s.lineTo(hw, hl);
  s.lineTo(0, 0);
  s.lineTo(-hw, hl);
  s.closePath();
  return s;
}

export default function NorthArrow({ size = 180, gridSection = 600, origin }: {
  size?: number;
  gridSection?: number;
  /** If provided, position relative to this [x, z] world corner instead of world origin. */
  origin?: [number, number];
}) {
  const arrowGeo = useMemo(() => {
    const shape = makeArrowShape(size, size * 0.4, size * 0.12, size * 0.35);
    const geo = new THREE.ShapeGeometry(shape);
    // Rotate geometry so the arrow lies on the XZ ground plane pointing +Z
    // ShapeGeometry produces XY — rotate -90° around X to lay flat
    geo.rotateX(-Math.PI / 2);
    // Center the arrow so it's near the origin
    geo.translate(0, 0.2, -size * 0.3);
    return geo;
  }, [size]);

  // "N" letter built from 3 flat shapes (left stroke, right stroke, diagonal)
  const nStrokes = useMemo(() => {
    const w = size * 0.18;
    const h = size * 0.25;
    const t = size * 0.04;

    const make = (shape: THREE.Shape) => {
      const g = new THREE.ShapeGeometry(shape);
      g.rotateX(-Math.PI / 2);
      g.translate(0, 0.3, size * 0.55);
      return g;
    };

    // Left vertical
    const ls = new THREE.Shape();
    ls.moveTo(-w / 2, 0); ls.lineTo(-w / 2 + t, 0);
    ls.lineTo(-w / 2 + t, h); ls.lineTo(-w / 2, h); ls.closePath();

    // Right vertical
    const rs = new THREE.Shape();
    rs.moveTo(w / 2 - t, 0); rs.lineTo(w / 2, 0);
    rs.lineTo(w / 2, h); rs.lineTo(w / 2 - t, h); rs.closePath();

    // Diagonal
    const ds = new THREE.Shape();
    ds.moveTo(-w / 2, h - t); ds.lineTo(-w / 2 + t, h);
    ds.lineTo(w / 2, t); ds.lineTo(w / 2 - t, 0); ds.closePath();

    return [make(ls), make(rs), make(ds)];
  }, [size]);

  const posX = origin ? origin[0] - gridSection : -gridSection;
  const posZ = origin ? origin[1] - gridSection : -gridSection;

  return (
    <group position={[posX, 0, posZ]} rotation={[0, Math.PI, 0]}>
      <mesh geometry={arrowGeo}>
        <meshBasicMaterial color={ARROW_COLOR} side={THREE.DoubleSide} />
      </mesh>
      {nStrokes.map((geo, i) => (
        <mesh key={i} geometry={geo}>
          <meshBasicMaterial color={LABEL_COLOR} side={THREE.DoubleSide} />
        </mesh>
      ))}
    </group>
  );
}
